import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { DevelopmentDetailSolicitationResponse } from '@/domain/models/development.js';
import type { SolicitationEvidenceProps } from '@/domain/models/solicitation-evidence.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { SolicitationCard } from '@/presentation/pages/developments/solicitations/solicitation-card.js';

interface Props {
    canManageSolicitations: boolean;
    canResolveSolicitation: boolean;
    canEvaluateSolicitation: boolean;
    solicitationError: string | null;
    solicitations: DevelopmentDetailSolicitationResponse[];
    approvingSolicitationId: string | null;
    onCreateNew: () => void;
    onApprove: (solicitationId: string) => void;
    onReject: (solicitationId: string) => void;
    onReclassify: (solicitationId: string) => void;
    onResolve: (solicitationId: string) => void;
    fetchEvidences: (solicitationId: string) => Promise<SolicitationEvidenceProps[]>;
    onDownloadEvidence: (solicitationId: string, docId: string) => Promise<void>;
    onRemoveEvidence: (solicitationId: string, docId: string) => Promise<boolean>;
}

export function SolicitationsTab({
    canManageSolicitations,
    canResolveSolicitation,
    canEvaluateSolicitation,
    solicitationError,
    solicitations,
    approvingSolicitationId,
    onCreateNew,
    onApprove,
    onReject,
    onReclassify,
    onResolve,
    fetchEvidences,
    onDownloadEvidence,
    onRemoveEvidence
}: Props) {
    const { translate } = useApp();

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {translate('solicitations.adjustmentRequests')}
                    </Typography>
                    {canManageSolicitations && (
                        <Button size="small" variant="contained" sx={{ color: 'white' }} onClick={() => onCreateNew()}>
                            {translate('solicitations.newRequest')}
                        </Button>
                    )}
                </Stack>
                {solicitationError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                        {solicitationError}
                    </Typography>
                )}
                {solicitations.length === 0 ? (
                    <Stack spacing={0.5}>
                        <Typography variant="body2" color="text.disabled">
                            {translate('solicitations.noRequestsYet')}
                        </Typography>
                        {canManageSolicitations && (
                            <Typography variant="caption" color="text.disabled">
                                {translate('solicitations.clickNewRequest')}
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <Stack spacing={1}>
                        {solicitations.map((s) => (
                            <SolicitationCard
                                key={s.id}
                                solicitation={s}
                                canManageSolicitations={canManageSolicitations}
                                canResolveSolicitation={canResolveSolicitation}
                                canEvaluateSolicitation={canEvaluateSolicitation}
                                actionLoading={{ approving: approvingSolicitationId === s.id }}
                                onApprove={onApprove}
                                onReject={(solicitationId) => onReject(solicitationId)}
                                onReclassify={(solicitationId) => onReclassify(solicitationId)}
                                onResolve={(solicitationId) => onResolve(solicitationId)}
                                fetchEvidences={fetchEvidences}
                                onDownloadEvidence={onDownloadEvidence}
                                onRemoveEvidence={onRemoveEvidence}
                                translate={translate}
                                evidenceLabels={{
                                    attachments: translate('solicitations.attachments'),
                                    downloadAttachment: translate('solicitations.downloadAttachment'),
                                    removeAttachment: translate('solicitations.removeAttachment')
                                }}
                                removeConfirmLabels={{ title: translate('solicitations.removeAttachmentConfirm') }}
                            />
                        ))}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}
